import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import { statusLabel } from '@/lib/member-display'
import { ProfileEmailVerify } from './profile-email-verify'
import { ProfilePhoneVerify } from './profile-phone-verify'
import type { ManageableMemberStatus, MemberDetail } from '@riverpark/types'

interface ProfileSettingsPanelProps {
  name: string
  email: string
  phone: string
  role: MemberDetail['role']
  status: ManageableMemberStatus
  onNameChange: (v: string) => void
  onEmailChange: (v: string) => void
  onPhoneChange: (v: string) => void
  onRoleChange?: (v: MemberDetail['role']) => void
  onStatusChange?: (v: ManageableMemberStatus) => void
  canManageRole: boolean
  canDelete: boolean
  memberStatus: MemberDetail['status']
  formError: string | null
  saved: boolean
  isPending: boolean
  isDeactivatePending: boolean
  onSubmit: (e: React.FormEvent) => void
  onDeactivate?: () => void
  onResendInvite?: () => void
  isResendPending?: boolean
  resendSuccess?: boolean
  isSelf: boolean
  savedPhone?: string
  phoneVerified?: boolean
  onPhoneVerifiedChange?: (verified: boolean) => void
  savedEmail?: string
  emailVerified?: boolean
  onEmailVerifiedChange?: (verified: boolean) => void
}

export function ProfileSettingsPanel({
  name,
  email,
  phone,
  role,
  status,
  onNameChange,
  onEmailChange,
  onPhoneChange,
  onRoleChange,
  onStatusChange,
  canManageRole,
  canDelete,
  memberStatus,
  formError,
  saved,
  isPending,
  isDeactivatePending,
  onSubmit,
  onDeactivate,
  onResendInvite,
  isResendPending,
  resendSuccess,
  isSelf,
  savedPhone,
  phoneVerified = true,
  onPhoneVerifiedChange,
  savedEmail,
  emailVerified = true,
  onEmailVerifiedChange,
}: ProfileSettingsPanelProps) {
  const phoneChangeBlocked = isSelf && savedPhone !== undefined && !phoneVerified
  const emailChangeBlocked = isSelf && savedEmail !== undefined && !emailVerified
  const contactChangeBlocked = phoneChangeBlocked || emailChangeBlocked
  return (
    <form onSubmit={onSubmit} className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>{isSelf ? 'Contact details' : 'Manage member'}</CardTitle>
          <p className="font-sans text-sm text-muted-foreground">
            {isSelf
              ? 'Keep your name, email, and phone up to date for invoices and estate notices.'
              : 'Update contact information. Role and status changes apply immediately.'}
          </p>
        </CardHeader>
        <CardContent className="space-y-4">
          <Input
            label="Full name"
            value={name}
            onChange={(e) => onNameChange(e.target.value)}
            required
          />
          <Input
            label="Email address"
            type="email"
            value={email}
            onChange={(e) => onEmailChange(e.target.value)}
            required
          />

          {isSelf && savedEmail && onEmailVerifiedChange && (
            <ProfileEmailVerify
              email={email}
              savedEmail={savedEmail}
              onVerifiedChange={onEmailVerifiedChange}
            />
          )}

          <Input
            label="Phone number"
            value={phone}
            onChange={(e) => onPhoneChange(e.target.value)}
            hint="Format: +254XXXXXXXXX"
            required
          />

          {isSelf && savedPhone && onPhoneVerifiedChange && (
            <ProfilePhoneVerify
              phone={phone}
              savedPhone={savedPhone}
              onVerifiedChange={onPhoneVerifiedChange}
            />
          )}

          {canManageRole && onRoleChange && onStatusChange && (
            <div className="space-y-4 border-t border-border-2 pt-4">
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-1.5">
                  <label className="font-sans text-sm font-medium text-ink" htmlFor="profile-role">
                    Role
                  </label>
                  <select
                    id="profile-role"
                    value={role}
                    onChange={(e) => onRoleChange(e.target.value as MemberDetail['role'])}
                    className="flex h-10 w-full rounded border border-input bg-card px-3.5 font-sans text-sm text-ink focus-visible:border-primary focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-green/20"
                  >
                    <option value="member">Member</option>
                    <option value="committee">Committee</option>
                    <option value="estate_manager">Estate manager</option>
                    <option value="admin">Admin</option>
                  </select>
                </div>
                <div className="space-y-1.5">
                  <span className="font-sans text-sm font-medium text-ink">Account status</span>
                  {memberStatus === 'invited' ? (
                    <div className="flex h-10 flex-wrap items-center gap-2">
                      <Badge variant="invited">{statusLabel('invited')}</Badge>
                      <span className="font-sans text-sm text-muted-foreground">
                        Pending invite acceptance
                      </span>
                    </div>
                  ) : (
                    <select
                      id="profile-status"
                      value={status}
                      onChange={(e) => onStatusChange?.(e.target.value as ManageableMemberStatus)}
                      className="flex h-10 w-full rounded border border-input bg-card px-3.5 font-sans text-sm text-ink focus-visible:border-primary focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-green/20"
                    >
                      <option value="active">Active</option>
                      <option value="inactive">Inactive</option>
                    </select>
                  )}
                </div>
              </div>
            </div>
          )}

          {formError && (
            <p className="font-sans text-sm text-destructive" role="alert">
              {formError}
            </p>
          )}
          {saved && (
            <p className="font-sans text-sm text-status-paid-text">Changes saved successfully.</p>
          )}
          {resendSuccess && (
            <p className="font-sans text-sm text-status-paid-text">
              Invitation email sent — link expires in 48 hours.
            </p>
          )}
        </CardContent>
      </Card>

      <div
        className={`flex flex-wrap gap-3 rounded border border-border-2 bg-card px-5 py-4 ${
          isSelf || memberStatus !== 'invited' ? 'items-center justify-between' : 'justify-end'
        }`}
      >
        {!isSelf && memberStatus !== 'invited' && (
          <p className="font-sans text-sm text-muted-foreground">
            Save to update this member record.
          </p>
        )}
        {isSelf && (
          <p className="font-sans text-sm text-muted-foreground">
            Changes apply to your portal account.
          </p>
        )}
        <div className="flex flex-wrap gap-2">
          {canManageRole && memberStatus === 'invited' && onResendInvite && (
            <Button
              type="button"
              variant="secondary"
              loading={isResendPending}
              onClick={onResendInvite}
            >
              Resend invitation
            </Button>
          )}
          <Button type="submit" loading={isPending} disabled={contactChangeBlocked}>
            Save changes
          </Button>
          {canDelete &&
            (memberStatus === 'active' || memberStatus === 'invited') &&
            onDeactivate && (
              <Button
                type="button"
                variant="destructive"
                loading={isDeactivatePending}
                onClick={onDeactivate}
              >
                {memberStatus === 'invited' ? 'Cancel invitation' : 'Deactivate'}
              </Button>
            )}
        </div>
      </div>
    </form>
  )
}
